import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { Actions, ofActionCompleted, Store } from '@ngxs/store';
import { Column } from '@miq/catalog';
import { ChangeColumnConfig } from '@records/state/current-catalog/change-column-config.action';
import { CurrentCatalogModel } from '@records/state/current-catalog/current-catalog.model';
import { CURRENT_CATALOG_STATE_TOKEN } from '@records/state/current-catalog/state-token';

/**
 * Provides handy observables around CurrentCatalogState
 */
@Injectable({
  providedIn: 'root',
})
export class CatalogStateStreamsService {
  /**
   * CurrentCatalogModel observable
   */
  private _onCatalogResolvedOrFetch$: Observable<CurrentCatalogModel>;

  constructor(
    private store: Store,
    private actions$: Actions
  ) {
    this._onCatalogResolvedOrFetch$ = this._makeUpCatalogLoadedOrFetchedStream(store);
  }

  private _makeUpCatalogLoadedOrFetchedStream(store: Store): Observable<CurrentCatalogModel> {
    // Live stream: emits every time the catalog transitions to loaded (covers re-navigation to same entity
    // where RetrieveCatalogInfo is not re-dispatched, so the catalog is already loaded in state).
    return store.select(CURRENT_CATALOG_STATE_TOKEN).pipe(filter(state => state.catalogLoaded));
  }

  /**
   * Returns and observable that streams CurrentCatalogStateModel when either catalog is first loaded
   * or when a RetrieveCatalogInfo action is completed
   */
  public get loadedOrFetched$(): Observable<CurrentCatalogModel> {
    return this._onCatalogResolvedOrFetch$;
  }

  /**
   * Emits a Column ref when given columnId receives or is target of a {@link ChangeColumnConfig} action
   *
   * @param columnId
   * @returns
   */
  public onChangeColumnConfig$(columnId: number) {
    // set up listener to apply columns changes
    return this.actions$.pipe(
      // listen every ChangeColumnConfig until destroy this component
      ofActionCompleted(ChangeColumnConfig),
      // but filter to only changes applying to this config
      /**
       * TO DO(from typescript update): remove "any" type and add publicly
       * accesible columnId field or getter to the ChangeColumnConfig interface,
       * as it currently is a private member.
       */
      filter((action: any) => action.action.columnId === columnId),
      // get the latest
      map(this._getConfigFromCatalogStateSnap(columnId))
    );
  }

  /**
   * Picks and returns the Column reference from CurrentCatalogModel snapshot
   * @param columnId
   * @returns
   */
  private _getConfigFromCatalogStateSnap = (columnId: number): (() => Column) => {
    return () => {
      const { columns } = this.store.selectSnapshot(CURRENT_CATALOG_STATE_TOKEN);
      return columns[columnId];
    };
  };
}
